import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import {
  approveJobQuote,
  createQuoteShareLink,
  getJobQuote,
  quoteQueryKey,
  respondJobQuote,
  sendJobQuote,
  updateJobQuote,
  type QuoteShareLinkDto,
  type UpdateQuoteRequest,
} from '@/lib/api/quotes';
import { customersQueryKey, listCustomers } from '@/lib/api/customers';

import { fromLineItems, lineItemsTotal, toLineItems } from './helpers';
import { LineItemsField } from './line-items-field';
import { QuoteDocument } from './quote-document';
import { ShareLinkPanel } from './share-link-panel';
import {
  editQuoteSchema,
  type EditQuoteInput,
  type QuoteFieldsValues,
} from './schema';

interface Props {
  businessId: string;
  jobId: string;
  quoteId: string;
  // Owner-only: the approve endpoint is business-owner-gated.
  canApprove: boolean;
  // Owner, or an employee with `create_quotes` — may edit line items / notes / total.
  canManage: boolean;
  // When true (opened right after creating this quote), auto-mints a share
  // link on open instead of waiting for the owner to click "Share Link".
  autoShareLink?: boolean;
  onClose: () => void;
}

const DEFAULT_VALUES: EditQuoteInput = {
  lineItems: [],
  notes: '',
  manualTotal: false,
  totalAmount: '',
};

// Self-contained (like `visit-crew-section.tsx`): owns its own detail query
// plus the update / send / approve / respond mutations. Toasts + refetch are
// handled globally by the MutationCache in `lib/query-client.ts`.
export function QuoteDetailModal({
  businessId,
  jobId,
  quoteId,
  canApprove,
  canManage,
  autoShareLink,
  onClose,
}: Props) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [shareLink, setShareLink] = useState<QuoteShareLinkDto | null>(null);

  const quoteQuery = useQuery({
    queryKey: quoteQueryKey(businessId, jobId, quoteId),
    queryFn: () => getJobQuote(businessId, jobId, quoteId),
  });

  // Only owners can list customers (endpoint is owner-gated — `canApprove` is
  // owner-only at both call sites). Used just to prefill the mail/SMS recipient
  // once a link has been minted; a missing contact degrades to a blank one.
  const customersQuery = useQuery({
    queryKey: customersQueryKey(businessId, 'all'),
    queryFn: () => listCustomers(businessId),
    enabled: canApprove && shareLink !== null,
  });

  const form = useForm<EditQuoteInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(editQuoteSchema),
  });

  useEffect(() => {
    if (!quoteQuery.data) return;
    const q = quoteQuery.data;
    const itemsTotal = lineItemsTotal(q.line_items);
    form.reset({
      lineItems: fromLineItems(q.line_items),
      notes: q.notes ?? '',
      // Default to "manual" whenever the stored total isn't just the line-item
      // sum (or there are no items to sum), so editing never silently rewrites it.
      manualTotal: q.line_items.length === 0 || itemsTotal !== q.total_amount,
      totalAmount: String(q.total_amount),
    });
  }, [quoteQuery.data, form]);

  const updateMutation = useMutation({
    mutationFn: (body: UpdateQuoteRequest) =>
      updateJobQuote(businessId, jobId, quoteId, body),
    meta: { successMessage: t.quoteUpdated },
    onSuccess: () => setEditing(false),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendJobQuote(businessId, jobId, quoteId),
    meta: { successMessage: t.quoteSentMsg },
  });

  // Minting also stamps `sent_at` server-side; the global MutationCache refetch
  // picks that up, so the "Sent" phase badge updates without extra wiring.
  const shareMutation = useMutation({
    mutationFn: () => createQuoteShareLink(businessId, jobId, quoteId),
    meta: { successMessage: t.quoteShareLinkReady },
    onSuccess: (data) => setShareLink(data),
  });

  // Opened right after creation: mint the share link immediately instead of
  // waiting for a separate "Share Link" click. Guarded so it fires once per
  // mount even under React's StrictMode double-invoke.
  const autoSharedRef = useRef(false);
  useEffect(() => {
    if (!autoShareLink || autoSharedRef.current) return;
    autoSharedRef.current = true;
    shareMutation.mutate();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [autoShareLink]);

  const approveMutation = useMutation({
    mutationFn: () => approveJobQuote(businessId, jobId, quoteId),
    meta: { successMessage: t.quoteApprovedMsg },
  });

  const respondMutation = useMutation({
    mutationFn: (status: 'accepted' | 'declined') =>
      respondJobQuote(businessId, jobId, quoteId, { status }),
    meta: { successMessage: t.quoteRespondedMsg },
    onSuccess: onClose,
  });

  // Two-click arm/confirm for Decline — matches the unassign idiom in
  // `visit-crew-section.tsx`; there's no shared confirm dialog.
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    };
  }, []);

  function requestDecline() {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    if (confirmingDecline) {
      setConfirmingDecline(false);
      respondMutation.mutate('declined');
      return;
    }
    setConfirmingDecline(true);
    confirmTimerRef.current = setTimeout(() => setConfirmingDecline(false), 4000);
  }

  function handleSave(data: EditQuoteInput) {
    updateMutation.mutate({
      line_items: toLineItems(data.lineItems),
      notes: data.notes ?? '',
      total_amount: data.manualTotal
        ? Number(data.totalAmount)
        : lineItemsTotal(data.lineItems),
    });
  }

  const quote = quoteQuery.data;
  const isPending = quote?.status === 'pending';
  const busy =
    updateMutation.isPending ||
    sendMutation.isPending ||
    shareMutation.isPending ||
    approveMutation.isPending ||
    respondMutation.isPending;

  const shareContact = quote
    ? customersQuery.data?.find(
        (c) => c.customer_user_id === quote.customer_user_id,
      )
    : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">
            {quote?.job_title ?? t.addQuoteTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {quoteQuery.isPending ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">{t.loading}</p>
        ) : quoteQuery.isError || !quote ? (
          <p className="px-6 py-8 text-sm text-destructive">{t.quoteLoadError}</p>
        ) : (
          <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-4">
            <QuoteDocument quote={quote} />

            {isPending && canManage && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  data-testid="quote-edit-toggle"
                  onClick={() => setEditing((v) => !v)}
                >
                  {editing ? t.quoteEditDone : t.quoteEdit}
                </Button>
              </div>
            )}

            {isPending && canManage && editing && (
              <form
                onSubmit={form.handleSubmit(handleSave)}
                className="flex flex-col gap-4 border-t pt-4"
              >
                <LineItemsField
                  control={form.control as unknown as Control<QuoteFieldsValues>}
                />

                <Controller
                  control={form.control}
                  name="notes"
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="quote-notes"
                        className="text-sm font-medium text-foreground"
                      >
                        {t.quoteNotes}
                      </label>
                      <textarea
                        {...field}
                        id="quote-notes"
                        rows={3}
                        placeholder={t.quoteNotesPlaceholder}
                        data-testid="quote-notes-input"
                        className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                      />
                      {fieldState.error && (
                        <p className="text-xs text-destructive">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={busy}
                    data-testid="quote-save"
                  >
                    {t.saveQuote}
                  </Button>
                </div>
              </form>
            )}

            {/* Actions — pending only */}
            {isPending && (
              <div className="flex flex-wrap gap-2 border-t pt-4">
                {!quote.sent_at && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => sendMutation.mutate()}
                    data-testid="quote-send"
                  >
                    {t.quoteSend}
                  </Button>
                )}
                {canManage && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => shareMutation.mutate()}
                    data-testid="quote-share-link"
                  >
                    {t.quoteShareLink}
                  </Button>
                )}
                {canApprove && !quote.approved_at && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => approveMutation.mutate()}
                    data-testid="quote-approve"
                  >
                    {t.quoteApprove}
                  </Button>
                )}
                <div className="flex-1" />
                <Button
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() => respondMutation.mutate('accepted')}
                  data-testid="quote-accept"
                >
                  {t.quoteAccept}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={busy}
                  onClick={requestDecline}
                  data-testid="quote-decline"
                >
                  {confirmingDecline ? t.quoteDeclineConfirm : t.quoteDecline}
                </Button>
              </div>
            )}

            {shareLink && (
              <ShareLinkPanel
                link={shareLink}
                recipientEmail={shareContact?.email}
                recipientPhone={shareContact?.phone}
                onClose={() => setShareLink(null)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
